import React, { memo, useCallback } from 'react';
import { NavLink } from 'react-router-dom';
import { useCart } from '../../hooks/useCart'; // Custom hook for cart management
import { useWishlist } from '../../hooks/useWishlist'; // Custom hook for wishlist
import FormateNumber from '../../helpers/FormateNumber';
import { Icon } from '@iconify/react';
import { toast } from 'react-toastify';

const Product = memo(({ title, productThumb, price, id, specs }) => {
  const { addToCart, isInCart } = useCart();
  const { addToWishlist, isInWishlist } = useWishlist();

  // Memoized handlers to prevent unnecessary re-renders
  const handleAddToCart = useCallback(async () => {
    try {
      if (isInCart(id)) {
        toast.info('Product already in cart');
        return;
      }

      await addToCart({
        id,
        title,
        price,
        quantity: 1,
        specs
      });
      
      // Store cart data in localStorage for persistence
      localStorage.setItem('lastAddedToCart', Date.now());
      toast.success('Added to cart');
    } catch (err) {
      toast.error('Failed to add to cart');
      console.error('Cart error:', err);
    }
  }, [id, title, price, specs, addToCart, isInCart]);

  const handleAddToWishlist = useCallback(async () => {
    try {
      if (isInWishlist(id)) {
        toast.info('Product already in wishlist');
        return;
      }

      await addToWishlist({
        id,
        title,
        price,
        specs
      });
      toast.success('Added to wishlist');
    } catch (err) {
      toast.error('Failed to add to wishlist');
      console.error('Wishlist error:', err);
    }
  }, [id, title, price, specs, addToWishlist, isInWishlist]);

  return (
    <div className="cs_product_card cs_style_1">
      <div className="cs_product_thumb">
        <img 
          src={productThumb} 
          alt={title}
          loading="lazy" // Lazy load images
        />
        <div className="cs_product_overlay" />
        <div className="cs_card_btns">
          <button 
            type="button"
            onClick={handleAddToCart}
            className={isInCart(id) ? 'cs_btn_active' : ''}
          >
            <Icon icon="fa6-solid:cart-shopping" />
          </button>
          <button 
            type="button"
            onClick={handleAddToWishlist}
            className={isInWishlist(id) ? 'cs_btn_active' : ''}
          >
            <Icon icon="fa6-solid:heart" />
          </button>
          <NavLink to={`/shop/${id}`}>
            <Icon icon="fa6-solid:eye" />
          </NavLink>
        </div>
      </div>
      <div className="cs_product_info">
        <h2 className="cs_product_title">
          <NavLink to={`/shop/${id}`}>{title}</NavLink>
        </h2>
        <p className="cs_product_price">
          Price: <FormateNumber number={price} />
        </p>
      </div>
    </div>
  );
});

export default Product;
